<script lang="ts">
    import { onDestroy, onMount, tick, createEventDispatcher } from "svelte"
    import * as maptilersdk from "@maptiler/sdk"
    import type { MapMouseEvent } from "@maptiler/sdk"
    import { GeocodingControl } from "@maptiler/geocoding-control/maptilersdk"
    import "@maptiler/sdk/dist/maptiler-sdk.css"
    import "@maptiler/geocoding-control/style.css"
    import { maptilerKey } from "../../../config"
    import CTA from "../CTA.svelte"

    const DEFAULT_LAT = 11.0299
    const DEFAULT_LONG = 50.9848
    const LOCK_DURATION = 1000
    const INPUT_SELECTOR = ".maplibregl-control-container input"

    type SelectedLocation = {
        lat: number
        long: number
        address: string | null
    }

    type LocationSelectedDetail = {
        lat: number
        long: number
    }

    type GeocodingPickDetail = {
        center: [number, number]
        place_name_de?: string
        place_name?: string
        name?: string
    }

    export let mapId = "map"
    export let onSelectLocation: (lat: number, long: number, address: string | null) => void = () => {}
    export let lat: number = DEFAULT_LAT
    export let long: number = DEFAULT_LONG
    export let name = ""

    let container: HTMLDivElement
    let map: maptilersdk.Map | null = null
    let marker: maptilersdk.Marker | null = null
    let geocodingControl: GeocodingControl | null = null
    let geocodingPickListener: ((event: Event) => void) | null = null
    let mapClickHandler: ((event: MapMouseEvent) => void) | null = null
    let ctaWrapper: HTMLDivElement
    let isLocked = false

    const dispatch = createEventDispatcher<{ locationSelected: LocationSelectedDetail }>()

    let selectedLocation: SelectedLocation | null =
        name || lat !== DEFAULT_LAT || long !== DEFAULT_LONG ? { lat, long, address: name || null } : null

    function notifyLocationChange(latValue: number, longValue: number, address: string | null) {
        selectedLocation = { lat: latValue, long: longValue, address }
        onSelectLocation(latValue, longValue, address)
        dispatch("locationSelected", { lat: latValue, long: longValue })
    }

    function openInGoogleMaps() {
        if (!selectedLocation) {
            return
        }
        const { lat: destinationLat, long: destinationLong, address } = selectedLocation
        const destinationQuery =
            address && address.trim().length > 0 ? encodeURIComponent(address) : `${destinationLat},${destinationLong}`
        if (typeof window !== "undefined") {
            const url = `https://www.google.com/maps/dir/?api=1&destination=${destinationQuery}`
            window.open(url, "_blank", "noopener,noreferrer")
        }
    }

    async function attachCtaToControl() {
        await tick()
        if (!container || !ctaWrapper) {
            return
        }

        const topLeftControl = container.querySelector<HTMLElement>(".maplibregl-ctrl-top-left")
        if (!topLeftControl) {
            if (typeof window !== "undefined") {
                window.requestAnimationFrame(attachCtaToControl)
            }
            return
        }

        if (!topLeftControl.contains(ctaWrapper)) {
            topLeftControl.appendChild(ctaWrapper)
            topLeftControl.classList.add("with-cta")
            ctaWrapper.classList.add("cta-visible")
        }
    }

    async function reverseGeocode(targetLat: number, targetLong: number): Promise<string | null> {
        const response = await fetch(
            `https://api.maptiler.com/geocoding/${targetLong},${targetLat}.json?key=${maptilerKey}`
        )
        const data = await response.json()
        if (data && Array.isArray(data.features) && data.features.length > 0) {
            const place = data.features[0].place_name as string
            const inputElement = document.querySelector<HTMLInputElement>(INPUT_SELECTOR)
            if (inputElement) {
                inputElement.value = place
            }
            return place
        }
        return null
    }

    function handleLocationSelection(event: CustomEvent<GeocodingPickDetail>) {
        if (isLocked) {
            return
        }
        isLocked = true
        const detail = event.detail
        if (detail?.center) {
            const [selectedLong, selectedLat] = detail.center
            const address = detail.place_name_de ?? detail.place_name ?? detail.name ?? null
            marker?.setLngLat([selectedLong, selectedLat])
            notifyLocationChange(selectedLat, selectedLong, address)
        }
        window.setTimeout(() => {
            isLocked = false
        }, LOCK_DURATION)
    }

    onMount(() => {
        maptilersdk.config.apiKey = maptilerKey
        map = new maptilersdk.Map({
            container: mapId,
            style: maptilersdk.MapStyle.STREETS,
            center: [long, lat],
            zoom: 12,
        })

        marker = new maptilersdk.Marker().setLngLat([long, lat]).addTo(map)

        geocodingControl = new GeocodingControl({
            showResultMarkers: true,
            marker: { color: "#e2010f" },
        })
        map.addControl(geocodingControl, "top-left")
        map.setLanguage("de")

        geocodingPickListener = (event: Event) => handleLocationSelection(event as CustomEvent<GeocodingPickDetail>)
        geocodingControl.addEventListener("pick", geocodingPickListener as EventListener)

        mapClickHandler = async (event: MapMouseEvent) => {
            const [clickedLong, clickedLat] = event.lngLat.toArray() as [number, number]
            marker?.setLngLat([clickedLong, clickedLat])
            const place = await reverseGeocode(clickedLat, clickedLong)
            notifyLocationChange(clickedLat, clickedLong, place)
        }
        map.on("click", mapClickHandler)

        attachCtaToControl()

        if (lat !== DEFAULT_LAT || long !== DEFAULT_LONG) {
            if (name) {
                const inputElement = document.querySelector<HTMLInputElement>(INPUT_SELECTOR)
                if (inputElement) {
                    inputElement.value = name
                }
            } else {
                reverseGeocode(lat, long).then((place) => {
                    if (place) {
                        selectedLocation = { lat, long, address: place }
                    }
                })
            }
        }
    })

    onDestroy(() => {
        if (geocodingControl && geocodingPickListener) {
            geocodingControl.removeEventListener("pick", geocodingPickListener as EventListener)
        }
        if (map && mapClickHandler) {
            map.off("click", mapClickHandler)
        }
        map?.remove()
        map = null
        marker = null
    })
</script>

<div class="mapwrapper">
    <div class="map-wrap">
        <div class="inner-wrapper">
            <div
                class="map"
                id="{mapId}"
                bind:this="{container}"
            ></div>
        </div>
        <div class="geocoding">
            <div
                class="cta-wrapper"
                bind:this="{ctaWrapper}"
            >
                <CTA
                    type="primaryButton"
                    content="Google Maps"
                    disabled="{!selectedLocation}"
                    on:click="{openInGoogleMaps}"
                />
            </div>
        </div>
    </div>
</div>

<style
    lang="less"
    global
>
    .mapwrapper {
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
        height: 100%;
        min-height: 400px;
        overflow: hidden;
        .maplibregl-ctrl-attrib-inner {
            display: none !important;
        }
        .map {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell",
                "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            border-radius: 12px;
        }

        code {
            font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
        }

        .map-wrap {
            position: relative;
            width: 100%;
            height: 100%;
            .inner-wrapper {
                width: 100%;
                height: 100%;
            }
            .maplibregl-canvas-container {
                height: calc(100% - 46px - 0.6rem) !important;
                width: 100%;
                min-width: 100%;
                display: flex;
                justify-content: center;
                canvas {
                    border: 1px solid var(--grey-200);
                    border-radius: 12px;
                }
            }
            .maplibregl-ctrl-top-left {
                position: absolute;
                top: calc((46px + 10px + 0.8rem) * -1);
                left: -10px;
                width: calc(100% + 10px);
                display: flex;
                flex-direction: column;
                justify-content: flex-end;

                form {
                    width: 100% !important;
                    height: 100%;
                    max-width: 100% !important;
                    border-radius: 12px;
                    border: 1px solid var(--grey-200) !important;
                    box-shadow: none !important;
                    background: var(--white-100);
                    height: 46px;
                    .selected {
                        animation: none !important;
                    }
                    .input-group {
                        width: 100%;
                        height: 100%;
                    }
                }
                * {
                    outline: none !important;
                    border: none !important;
                }
            }
            .maplibregl-ctrl-top-left.with-cta {
                flex-direction: row;
                align-items: stretch;
                gap: 0.6rem;
            }
            .maplibregl-ctrl-top-left.with-cta > .maplibregl-ctrl {
                flex: 1 1 auto;
                min-width: 0;
                display: flex;
                position: relative;
                z-index: 3;
            }
            .maplibregl-ctrl-top-left.with-cta form {
                flex: 1 1 auto;
                width: 100% !important;
                min-width: 0;
            }
            .maplibregl-ctrl-top-left.with-cta .cta-wrapper {
                display: flex;
                flex: 0 0 auto;
                align-items: center;
                position: relative;
                z-index: 5;
                pointer-events: auto;
            }
            .maplibregl-ctrl-top-left.with-cta .cta-wrapper :global(.primaryButton) {
                height: 46px;
                border-radius: 12px;
                padding: 0 1.2rem;
                display: flex;
                align-items: center;
            }
            .geocoding {
                position: absolute;
                top: 10px;
                left: 10px;
            }
            .map {
                position: absolute;
                width: 100%;
                height: 100%;
                margin-top: calc(46px + 1.2rem);
                overflow: visible;
            }
        }
    }
    .cta-wrapper {
        display: none;
    }
    .cta-wrapper.cta-visible {
        display: flex;
    }
</style>
